import { useState, useMemo, useEffect } from 'react';

import {
  ComposedChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

function TransactionChart({ transactions }) {

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');

  const toInputDate = (d) => d.toISOString().split('T')[0];

  useEffect(() => {
    if (transactions.length === 0) return;
    const allDates = transactions.map(t => new Date(t.date));
    const min = toInputDate(new Date(Math.min(...allDates)));
    const max = toInputDate(new Date(Math.max(...allDates)));
    setMinDate(min);
    setMaxDate(max);
    setDateFrom(min);
    setDateTo(max);
  }, [transactions]);

  const filtered = useMemo(() => {
    return transactions.filter(t => {
      const date = new Date(t.date);
      return date >= new Date(dateFrom) && date <= new Date(dateTo);
    });
  }, [transactions, dateFrom, dateTo]);

  // Group transactions by month
  const grouped = {};

  filtered.forEach(t => {
    const date = new Date(t.date);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!grouped[key]) grouped[key] = { month: key, income: 0, expense: 0 };

    const amount = Number(t.amount);
    if (t.type === 'income') grouped[key].income += amount;
    if (t.type === 'expense') grouped[key].expense += Math.abs(amount);
  });

  const data = Object.values(grouped)
    .sort((a, b) => a.month.localeCompare(b.month))
    .map(d => ({
      ...d,
      net: d.income - d.expense,
      month: new Date(d.month + '-01').toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })
    }));

  const cumulativeData = data.reduce((acc, month, i) => {
    const prev = i === 0 ? 0 : acc[i - 1].cumulative;
    acc.push({ ...month, cumulative: prev + month.net });
    return acc;
  }, []);

  const fmt = (v) => `$${Math.abs(v).toLocaleString('en-AU', { minimumFractionDigits: 0 })}`;

  if (transactions.length === 0) return null;

  const inputStyle = {
    padding: '6px 10px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'var(--bg)',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '13px',
    color: 'var(--text)',
    outline: 'none'
  };

  const labelStyle = {
    fontFamily: 'DM Mono, monospace',
    fontSize: '10px',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    letterSpacing: '0.5px',
    marginBottom: '4px'
  };

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: '24px',
      marginBottom: '28px'
    }}>

      {/* Date filter controls */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={labelStyle}>From</div>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={labelStyle}>To</div>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
        </div>
        <button
          onClick={() => { setDateFrom(minDate); setDateTo(maxDate); }}
          style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'transparent', fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--muted)', cursor: 'pointer' }}
        >
          reset
        </button>
      </div>

      {/* Chart 1 - Income & Expenses */}
      <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '20px' }}>
        Income &amp; Expenses Over Time
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e8e6e1" vertical={false} />
          <XAxis dataKey="month" tick={{ fontFamily: 'DM Mono, monospace', fontSize: 11, fill: '#9b9690' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontFamily: 'DM Mono, monospace', fontSize: 11, fill: '#9b9690' }} axisLine={false} tickLine={false} tickFormatter={fmt} />
          <Tooltip
            formatter={(value, name) => [fmt(value), name]}
            contentStyle={{ fontFamily: 'DM Mono, monospace', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'white' }}
          />
          <Legend wrapperStyle={{ fontFamily: 'DM Mono, monospace', fontSize: '11px' }} />
          <Bar dataKey="income" name="Income" fill="#dcfce7" stroke="#16a34a" strokeWidth={1} radius={[3, 3, 0, 0]} />
          <Bar dataKey="expense" name="Expenses" fill="#fee2e2" stroke="#dc2626" strokeWidth={1} radius={[3, 3, 0, 0]} />
          <Line dataKey="net" name="Net" type="monotone" stroke="#2563eb" strokeWidth={2} dot={{ r: 3, fill: '#2563eb' }} />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Chart 2 - Cumulative Net */}
      <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--muted)', margin: '28px 0 20px' }}>
        Accumulated Net Over Time
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={cumulativeData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e8e6e1" vertical={false} />
          <XAxis dataKey="month" tick={{ fontFamily: 'DM Mono, monospace', fontSize: 11, fill: '#9b9690' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontFamily: 'DM Mono, monospace', fontSize: 11, fill: '#9b9690' }} axisLine={false} tickLine={false} tickFormatter={fmt} />
          <Tooltip
            formatter={(value) => [fmt(value), 'Cumulative Net']}
            contentStyle={{ fontFamily: 'DM Mono, monospace', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'white' }}
          />
          <defs>
            <linearGradient id="netGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area dataKey="cumulative" name="Cumulative Net" type="monotone" stroke="#2563eb" strokeWidth={2} fill="url(#netGradient)" dot={{ r: 3, fill: '#2563eb' }} />
        </ComposedChart>
      </ResponsiveContainer>

    </div>
  );
}

export default TransactionChart;